import React, { useState, useEffect } from 'react';
import { LuPlus, LuX } from 'react-icons/lu';
import useSchoolStore from '../../store/schoolStore';
import { useBulkReportStore } from '../../store/bulkReportStore';
import { BulkReportType } from '../../models/bulk_report';
import { getClassName } from '../../utils/schoolUtils';
import Button from './Button';
import Notice from './Notice';
import { Field, Select } from './FormControls';

const CreateBulkReportDialog = ({
  isOpen,
  onClose
}) => {
  const { getClasses, classes, isClassesLoading, schoolId } = useSchoolStore();
  const { createBulkReport, isBulkReportCreating, error } = useBulkReportStore();
  
  const [formValues, setFormValues] = useState({
    reportType: '',
    classList: [],
    selectedClass: '',
    selectedSection: ''
  });
  const [formErrors, setFormErrors] = useState({});

  // Load classes when dialog opens
  useEffect(() => {
    if (isOpen) {
      // Reset form
      setFormValues({
        reportType: '',
        classList: [],
        selectedClass: '',
        selectedSection: ''
      });
      setFormErrors({});
      
      // Load classes if not already loaded
      if (!classes || classes.length === 0) {
        getClasses();
      }
    }
  }, [isOpen, getClasses, classes]);

  // Convert BulkReportType values to human-friendly text
  const getReportTypeDisplayName = (type) => {
    switch (type) {
      case BulkReportType.STUDENT_PERFORMANCE:
        return 'Student performance report';
      default:
        return type;
    }
  };

  // Get available report types
  const reportTypes = Object.values(BulkReportType).map(type => ({
    value: type,
    label: getReportTypeDisplayName(type)
  }));

  // Get available sections for selected class
  const getAvailableSections = () => {
    if (!formValues.selectedClass || !classes) return [];
    
    // Convert both values to strings for comparison to handle type mismatches
    const selectedClass = classes.find(classItem => String(classItem.Class) === String(formValues.selectedClass));
    
    return selectedClass && selectedClass.sections ? selectedClass.sections : [];
  };

  const handleInputChange = (field, value) => {
    // Convert Class value back to number to match the original data type
    const processedValue = field === 'selectedClass' ? Number(value) : value;
    
    setFormValues(prev => ({
      ...prev,
      [field]: processedValue,
      // Reset section when class changes
      ...(field === 'selectedClass' ? { selectedSection: '' } : {})
    }));
    
    // Clear error for this field
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleAddClassSection = () => {
    if (!formValues.selectedClass || !formValues.selectedSection) {
      return;
    }

    // Check for duplicates
    const isDuplicate = formValues.classList.some(item => 
      item.Class === formValues.selectedClass && item.sec === formValues.selectedSection
    );

    if (isDuplicate) {
      return; // Don't add if it's a duplicate
    }

    // Add to classList (keep selectedClass and selectedSection for easy re-selection)
    const newClassList = [...formValues.classList, { 
      Class: formValues.selectedClass, 
      sec: formValues.selectedSection 
    }];
    
    setFormValues(prev => ({
      ...prev,
      classList: newClassList
    }));
    
    // Clear error for classList field
    if (formErrors.classList) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.classList;
        return newErrors;
      });
    }
  };

  const handleRemoveClassSection = (index) => {
    const newClassList = formValues.classList.filter((_, itemIndex) => itemIndex !== index);
    setFormValues(prev => ({
      ...prev,
      classList: newClassList
    }));
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formValues.reportType) {
      errors.reportType = 'Report type is required';
    }
    
    if (!formValues.classList || formValues.classList.length === 0) {
      errors.classList = 'At least one class-section is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    try {
       createBulkReport({
        reportType: formValues.reportType,
        classList: formValues.classList
      });
      onClose();
    } catch (error) {
      console.error('Error creating bulk report:', error);
    }
  };

  if (!isOpen) {
    return null;
  }

  const errorControlClass = 'border-red-500 focus:border-red-500 focus:ring-red-500/20';

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="bulk-report-title">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div className="relative flex max-h-[85vh] w-full max-w-lg animate-enter flex-col rounded-xl border border-ui-line bg-ui-card shadow-pop">
        <div className="flex items-start justify-between gap-4 border-b border-ui-line-soft px-6 py-4">
          <div>
            <h2 id="bulk-report-title" className="text-base font-semibold text-ui-ink">Create bulk report</h2>
            <p className="mt-0.5 text-sm text-ui-ink-2">Choose the kind of report and the classes to include.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-2 flex size-8 shrink-0 items-center justify-center rounded-lg text-ui-ink-3 transition-colors hover:bg-ui-hover hover:text-ui-ink"
          >
            <LuX aria-hidden className="size-4" />
          </button>
        </div>

        <div className="ui-scroll flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-6 py-5">
          <Field label="Report type">
            <Select
              value={formValues.reportType}
              onChange={(event) => handleInputChange('reportType', event.target.value)}
              className={formErrors.reportType ? errorControlClass : ''}
            >
              <option value="">Select report type</option>
              {reportTypes.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            {formErrors.reportType && <span className="text-xs text-red-600 dark:text-red-400">{formErrors.reportType}</span>}
          </Field>

          <div>
            <p className="mb-1.5 text-[13px] font-medium text-ui-ink-2">Classes</p>
            <div className="grid grid-cols-[1fr_1fr_auto] items-end gap-2">
              <Select
                value={formValues.selectedClass}
                onChange={(event) => handleInputChange('selectedClass', event.target.value)}
                disabled={isClassesLoading}
                aria-label="Class"
              >
                <option value="">{isClassesLoading ? 'Loading…' : 'Class'}</option>
                {classes.map((classItem) => (
                  <option key={classItem.Class} value={classItem.Class}>
                    {getClassName(schoolId, classItem.Class)}
                  </option>
                ))}
              </Select>
              <Select
                value={formValues.selectedSection}
                onChange={(event) => handleInputChange('selectedSection', event.target.value)}
                disabled={!formValues.selectedClass}
                aria-label="Section"
              >
                <option value="">Section</option>
                {getAvailableSections().map((section) => (
                  <option key={section} value={section}>
                    {section}
                  </option>
                ))}
              </Select>
              <Button
                onClick={handleAddClassSection}
                variant="secondary"
                disabled={!formValues.selectedClass || !formValues.selectedSection}
                leftIcon={<LuPlus className="size-4" />}
              >
                Add
              </Button>
            </div>

            {formValues.classList.length > 0 ? (
              <ul className="mt-3 flex flex-wrap gap-2">
                {formValues.classList.map((item, index) => (
                  <li key={index} className="flex items-center gap-1 rounded-md bg-ui-brand-soft py-1 pr-1 pl-2.5 text-sm text-ui-brand-soft-ink">
                    {getClassName(schoolId, item.Class)} {item.sec}
                    <button
                      type="button"
                      onClick={() => handleRemoveClassSection(index)}
                      aria-label={`Remove ${getClassName(schoolId, item.Class)} ${item.sec}`}
                      className="flex size-5 items-center justify-center rounded transition-colors hover:bg-black/10 dark:hover:bg-white/10"
                    >
                      <LuX aria-hidden className="size-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-2 text-xs text-ui-ink-3">Pick a class and section, then press Add. Add as many as you need.</p>
            )}

            {formErrors.classList && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{formErrors.classList}</p>}
          </div>

          {error && (
            <Notice tone="error">{error}</Notice>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-ui-line-soft px-6 py-4">
          <Button onClick={onClose} variant="secondary" disabled={isBulkReportCreating}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isBulkReportCreating} loading={isBulkReportCreating}>
            Create report
          </Button>
        </div>
      </div>
    </div>
  );
};
export default CreateBulkReportDialog;